import styled from "styled-components";
import { Button } from "../../App.styles";

const defaultWidth = 250;
const minItemWidth = 250;
const minItemHeight = 325;

const transparencyPattern = `
    background-color: #ffffff;
    background-image:
        linear-gradient(45deg, #d4d4d4 25%, transparent 25%),
        linear-gradient(-45deg, #d4d4d4 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, #d4d4d4 75%),
        linear-gradient(-45deg, transparent 75%, #d4d4d4 75%);
    background-size: 16px 16px;
    background-position: 0 0, 0 8px, 8px -8px, -8px 0;
`;

/**
 * Carousel nubs must stay at least 250px wide. The 32% container share can
 * shrink below that, so floor the result.
 * @param {number} [containerWidth]
 * @returns {string}
 */
export const getItemWidth = containerWidth => {
    const itemGap = 6;
    const itemWidth = containerWidth
        ? .32 * containerWidth - itemGap
        : defaultWidth;
    return `${Math.max(Math.min(itemWidth, defaultWidth), minItemWidth)}px`;
};

export const UploadBody = styled('label', {
    shouldForwardProp: (props) => !['container'].includes(props),
})(({ theme, container, isDisabled }) => {
    const itemWidth = getItemWidth(container?.clientWidth);
    
    return `
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        border-radius: ${theme.rounded.md};
        border: 2px dashed ${theme.primaryColor};
        background: ${theme.cardColor}50;
        width: ${defaultWidth}px;
        height: ${minItemHeight}px;
        min-height: ${minItemHeight}px;
        position: absolute;
        bottom: 0;
        left: 0;
        cursor: ${isDisabled ? 'not-allowed' : 'pointer'};

        .svg-inline--fa:not([data-icon="question-circle"]) {
            width: 72px;
            height: 50px;
            color: ${theme.primaryColor};
            opacity: ${isDisabled ? 0.65 : 1};
        }

        .btn {
            border-radius: ${theme.rounded.xs};
            display: block;
            width: 100%;
            white-space: normal;

            span {
                text-transform: capitalize;
            }
        }

        small {
            margin-top: 0.5rem;
            display: block;
        }

        ${theme.breakpoints.up('sm')} {
            width: ${itemWidth};
            min-width: ${itemWidth};
            height: auto;
            min-height: ${minItemHeight}px;
            position: relative;
            bottom: auto;
            right: auto;

            .btn.btn--flat {
                padding: .625rem .5rem .5rem .5rem;
            }
        }
    `;
});

export const ImageContainer = styled.div(({ theme }) => `
    width: 100%;
    max-width: 280px;
    overflow: auto;
    display: flex;

    ${theme.breakpoints.up('sm')} {
        max-width: none;
    }
`);

export const ImageBody = styled('div', {
    shouldForwardProp: (props) => !['container'].includes(props),
})(({ theme }) => {
    return `
        display: flex;
        padding-bottom: calc(${minItemHeight}px + 20px);
        flex-wrap: wrap;
        gap: 12px;

        ${theme.breakpoints.up('sm')} {
            padding-bottom: 0;
            flex-wrap: nowrap;
        }
    `;
});

export const ImageLoader = styled.div(({ isLoading }) => `
    position: absolute;
    top: 0;
    left: 0;
    transform: none;
    width: 100%;
    height: 100%;
    background: rgba(255,255,255,.75);
    display: ${isLoading ? 'flex' : 'none'};
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 10;
`);

export const ImageUploadWrapper = styled.div(({ theme }) => `
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    position: relative;
    margin-bottom: 1rem;

    .form-label {
        font-size: 0.8rem;
        margin-bottom: 0.75rem;
    }
`);

export const FileWrapper = styled.div`
    margin-right: 0.5rem;

    input[type="file"] {
        display: none;
    }

    .form-control {
        font-size: 0.8rem;
    }
`;

export const ActionWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    width: 100%;

    button {
        padding: .5rem .75rem;
    }
`;

export const UploadBtn = styled.div(({ theme }) => `
    display: inline-block;
    border-radius: ${theme.rounded.sm};
    width: 100%;

    &:focus {
        outline: 2px solid ${theme.accent2Color}
    }

    .btn.btn-primary {
        margin-bottom: 0;
    }
`);

export const ImagePreviewWrapper = styled('div', {
    shouldForwardProp: (props) => !['container', 'isLoading'].includes(props),
})(({ theme, container, isLoading }) => {
    const itemWidth = getItemWidth(container?.clientWidth);
    
    return `
        position: relative;
        display: flex;
        border-radius: ${theme.rounded.md};
        border: ${isLoading ? `none` : `2px solid ${theme.primaryColor}`};
        background: ${isLoading ? `transparent` : theme.white};
        overflow: hidden;
        min-width: ${defaultWidth}px;
        min-height: ${minItemHeight}px;
        max-height: 400px;
        justify-content: center;
        align-items: center;

        &:hover .image-action__buttons {
            top: 12px;
            opacity: 1;
        }

        ${theme.breakpoints.up('sm')} {
            min-width: ${itemWidth};
            height: auto;
            min-height: ${minItemHeight}px;
        }
    `;
});

export const NubsItem = styled('div', {
    shouldForwardProp: (props) => !['isImage', 'container', 'stickerify'].includes(props),
})(({ theme, isImage, container, stickerify }) => {
    const itemWidth = getItemWidth(container?.clientWidth);
    return `
        background: ${theme.white};
        ${stickerify ? transparencyPattern : ''}
        border-radius: ${theme.rounded.md};
        padding: ${isImage ? '0' : '12px'};
        min-height: ${minItemHeight}px;
        max-height: 400px;
        min-width: ${isImage ? 'unset' : `${defaultWidth}px`};
        overflow: ${isImage ? 'unset' : 'auto'};
        display: flex;
        align-items: center;

        .nubs__image {
            min-width: ${defaultWidth}px;
            width: 100%;
            height: 100%;
            object-fit: contain;
        }

        .image-thumbnail-loader__preview {
            ${stickerify ? 'background: transparent;' : ''}
            margin-right: 0;
            margin-bottom: 0;
            overflow: hidden;
            border-radius: calc(${theme.rounded.md} - 2px);
            max-height: 100%;
            min-height: ${minItemHeight}px;
            width: 100%;
        }

        ${theme.breakpoints.up('sm')} {
            min-width: ${isImage ? 'unset' : `${itemWidth}`};

            .nubs__image {
                min-width: ${itemWidth};
                width: ${itemWidth};
            }
        }

        &:hover .image-action__buttons {
            top: 12px;
            opacity: 1;
        }
    `;
});

export const ImagePlaceholder = styled('div', {
    shouldForwardProp: (props) => !['container', 'isError'].includes(props),
})(({ theme, container, isError }) => {
    const itemWidth = getItemWidth(container?.clientWidth);

    return `
        background: ${theme.cardColor}66;
        border-radius: ${theme.rounded.md};
        border: 2px dashed ${isError ? theme.dangerColor : theme.primaryColor};
        width: ${defaultWidth}px;
        min-height: ${minItemHeight}px;
        height: ${minItemHeight}px;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        color: ${isError ? theme.dangerColor : theme.primaryColor};
        opacity: ${isError ? 1 : 0.4};
        flex-direction: column;
        gap: 12px;
        padding: 16px;
        overflow: auto;

        ${isError ? `
            background: ${theme.white};
        ` : ''}

        ${theme.breakpoints.up('sm')} {
            min-width: ${itemWidth};
            min-height: ${minItemHeight}px;
            height: auto;
        }
    `;
});

export const ImageActionButtons = styled.div(({ theme }) => `
    position: absolute;
    opacity: 0;
    top: -24px;
    right: 12px;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 16px;
    background: ${theme.primaryColor};
    border-radius: 6px;
    padding: 0 12px;
    transition: opacity, top .25s;
`);

export const ImageActionButton = styled(Button)(({ theme }) => `
    padding: 0;
    font-size: 24px;
    
    &.btn-link {
        color: ${theme.white};

        &:hover {
            background: transparent;
        }
    }

    svg {
        font-size: 20px;
    }
`);

/**
 * Keeps ImageThumbnailLoader in the DOM while the generating placeholder is
 * visible so the thumbnail can load (and retry) without affecting layout.
 */
export const HiddenImageProbe = styled.div`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
    pointer-events: none;
`;
